import { filePreviewBase64 } from './pdfConvert';
import { readFileAsBase64 } from '../api/vault';

export const AI_CHAT_ACCEPT =
  'image/*,.pdf,.txt,.md,.csv,.json,.html,.xml,text/plain,text/markdown,application/pdf';

export const AI_CHAT_MAX_FILES = 5;
export const AI_CHAT_MAX_BYTES = 5 * 1024 * 1024;

export interface AiChatAttachmentDraft {
  id: string;
  fileName: string;
  mimeType: string;
  kind: 'image' | 'document';
  size: number;
  previewUrl?: string;
}

export interface AiChatAttachmentPayload {
  fileName: string;
  mimeType: string;
  kind: 'image' | 'document';
  dataBase64?: string;
  textContent?: string;
}

const TEXT_EXTENSIONS = new Set(['txt', 'md', 'csv', 'json', 'html', 'xml', 'log']);

function fileExtension(name: string) {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function isTextDocument(file: File) {
  if (file.type.startsWith('text/')) return true;
  return TEXT_EXTENSIONS.has(fileExtension(file.name));
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });
}

export function validateAiChatFile(file: File) {
  if (file.size > AI_CHAT_MAX_BYTES) {
    throw new Error(`${file.name} exceeds the 5 MB limit.`);
  }

  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';
  const isText = isTextDocument(file);

  if (!isImage && !isPdf && !isText) {
    throw new Error(`${file.name} is not supported. Use images, PDF, or text documents.`);
  }
}

export async function prepareAiChatAttachment(file: File): Promise<AiChatAttachmentPayload> {
  validateAiChatFile(file);

  if (isTextDocument(file)) {
    const textContent = (await readFileAsText(file)).slice(0, 12000);
    return {
      fileName: file.name,
      mimeType: file.type || 'text/plain',
      kind: 'document',
      textContent,
    };
  }

  if (file.type.startsWith('image/')) {
    return {
      fileName: file.name,
      mimeType: file.type,
      kind: 'image',
      dataBase64: await readFileAsBase64(file),
    };
  }

  const preview = await filePreviewBase64(file);
  if (!preview) {
    throw new Error(`Could not prepare ${file.name} for AI context.`);
  }

  return {
    fileName: file.name,
    mimeType: 'image/jpeg',
    kind: 'image',
    dataBase64: preview,
  };
}

export function createAttachmentDraft(file: File): AiChatAttachmentDraft {
  const kind = file.type.startsWith('image/') || file.type === 'application/pdf' ? 'image' : 'document';
  const previewUrl = kind === 'image' && file.type.startsWith('image/')
    ? URL.createObjectURL(file)
    : undefined;

  return {
    id: `${file.name}-${file.size}-${file.lastModified}`,
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    kind,
    size: file.size,
    previewUrl,
  };
}

export function revokeAttachmentDrafts(drafts: AiChatAttachmentDraft[]) {
  drafts.forEach((draft) => {
    if (draft.previewUrl) URL.revokeObjectURL(draft.previewUrl);
  });
}
